import { isSlowNetwork } from '@/utils/networkDetection';

export interface RecordedVideo {
  file: File;
  url: string;
  duration: number;
  name: string;
}

export type AspectRatio = '16:9' | '9:16';

export interface UploadProgress {
  progress: number;
  message: string;
  phase: string;
}

export const RECORDING_CONFIG = {
  BASE_VIDEO_BITRATE: 1_500_000, // 1.5 Mbps (server transcodes with FFmpeg — extra bitrate is wasted upload time)
  BASE_AUDIO_BITRATE: 128_000, // 128 kbps
  CHUNK_INTERVAL: 1000, // 1 second chunks for better memory management
  DEFAULT_MAX_DURATION: 120, // 2 minutes
  MAX_RECORDING_RESOLUTION: 1080, // Hard ceiling regardless of plan
  MOBILE_MAX_RESOLUTION: 720, // Mobile devices capped at 720p to reduce file size
  MOBILE_VIDEO_BITRATE: 800_000, // 800 kbps — mobile on WiFi
  MOBILE_SLOW_VIDEO_BITRATE: 400_000, // 400 kbps — cellular or slow network
  MOBILE_AUDIO_BITRATE: 64_000, // 64 kbps — half of desktop
  MOBILE_FRAME_RATE: 24, // Middle ground — smooth without doubling upload size vs browser default
} as const;

export function getVideoBitrate(resolution: number): number {
  if (resolution <= 480) return 1_000_000;
  if (resolution <= 720) return 1_500_000;
  return 3_000_000;
}

export function getAdaptiveBitrate(
  resolution: number,
  isMobile: boolean,
): { videoBitrate: number; audioBitrate: number } {
  if (!isMobile) {
    return { videoBitrate: getVideoBitrate(resolution), audioBitrate: RECORDING_CONFIG.BASE_AUDIO_BITRATE };
  }

  const slow = isSlowNetwork();

  return {
    videoBitrate: slow ? RECORDING_CONFIG.MOBILE_SLOW_VIDEO_BITRATE : RECORDING_CONFIG.MOBILE_VIDEO_BITRATE,
    audioBitrate: RECORDING_CONFIG.MOBILE_AUDIO_BITRATE,
  };
}
